import type { InventoryCommentV2 } from '../services/inventory-comments-v2.service'
import type { IInventoryComment } from './inventory'

/**
 * Un comentario tal como lo dibuja el panel, sin rastro de qué API lo trajo.
 *
 * La razón de que exista: v1 y v2 no comparten sistema de identidad. v1 identifica al autor con el
 * `_id` de Mongo y v2 con un `usr_`, así que «¿este comentario es mío?» se responde distinto de
 * cada lado. Si esa pregunta viviera en el componente, el componente tendría que saber de qué API
 * vino cada comentario —y comparar el id equivocado NO rompe nada visible: simplemente esconde el
 * botón de eliminar y nadie se entera hasta que un asesor reclama.
 *
 * Por eso `isOwn` se resuelve ACÁ, donde todavía se sabe qué identidad se está mirando. El
 * componente pregunta si puede borrar, no quién es el dueño.
 *
 * `id` es lo que la mutación de borrado necesita: el `_id` en v1, el `icm_` en v2. Cada traductor
 * lo llena con el que su API sabe recibir.
 */
export type InventoryCommentView = {
  id: string
  body: string
  authorName: string
  /** Semilla del avatar: identidad si la hay, si no el nombre. */
  authorSeed: string
  createdAt: string
  isOwn: boolean
  replies: InventoryCommentView[]
}

/** Traduce el árbol v1, donde el autor es un `_id` de Mongo. */
export function toCommentViewFromV1(
  comment: IInventoryComment,
  currentUserMongoId: string | undefined
): InventoryCommentView {
  const authorId = comment.author?._id
  return {
    id: comment._id,
    body: comment.body,
    authorName: comment.author?.fullname ?? 'Usuario',
    authorSeed: authorId ?? comment.author?.fullname ?? '',
    createdAt: comment.createdAt,
    // Sin autor no hay dueño: un comentario huérfano no es de nadie, ni siquiera del que mira.
    isOwn: Boolean(authorId) && authorId === currentUserMongoId,
    replies: (comment.replies ?? []).map((reply) => toCommentViewFromV1(reply, currentUserMongoId))
  }
}

/** Traduce el árbol v2, donde el autor es un `usr_`. */
export function toCommentViewFromV2(
  comment: InventoryCommentV2,
  currentUserPublicId: string | undefined
): InventoryCommentView {
  const authorId = comment.author?.id
  return {
    id: comment.id,
    body: comment.body,
    authorName: comment.author?.name ?? 'Usuario',
    authorSeed: authorId ?? comment.author?.name ?? '',
    createdAt: comment.createdAt,
    isOwn: Boolean(authorId) && authorId === currentUserPublicId,
    // v2 anida un solo nivel y las respuestas nunca traen las suyas.
    replies: (comment.replies ?? []).map((reply) => ({
      id: reply.id,
      body: reply.body,
      authorName: reply.author?.name ?? 'Usuario',
      authorSeed: reply.author?.id ?? reply.author?.name ?? '',
      createdAt: reply.createdAt,
      isOwn: Boolean(reply.author?.id) && reply.author?.id === currentUserPublicId,
      replies: []
    }))
  }
}
